import { useEffect, useMemo, useRef, useState } from "react";
import { Search } from "lucide-react";

import { Avatar, Badge, ListGroup, ListRow, getInitials, tintForId } from "@/ui";
import {
  useLeaderboardInfinite,
  useMyLeaderboardRank,
} from "@/features/queries";
import { useOpenUserProfile } from "@/features/users/UserProfileSheetProvider";
import { noAutofill } from "@/lib/noAutofill";
import type { LeaderboardEntry } from "@/types/api";

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 250;

export function CommunityPage() {
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const openProfile = useOpenUserProfile();

  // Debounce so each keystroke doesn't spam the server. The value we feed
  // to the query is the debounced one; the input stays snappy on `query`.
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(query), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [query]);

  const {
    data,
    isLoading,
    isPlaceholderData,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useLeaderboardInfinite(debounced, PAGE_SIZE);

  const { data: myRank } = useMyLeaderboardRank();

  const entries = useMemo<LeaderboardEntry[]>(
    () => data?.pages.flat() ?? [],
    [data],
  );

  const isSearching = debounced.trim().length > 0;
  // Top-3 medals only make sense on the unfiltered first page.
  const top3 = !isSearching ? entries.slice(0, 3) : [];
  const rest = !isSearching ? entries.slice(3) : entries;

  // Auto-load-more sentinel. Scroll container is the page body; a short
  // IntersectionObserver near the bottom of the list triggers the next page.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasNextPage || isFetchingNextPage) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) void fetchNextPage();
      },
      { rootMargin: "400px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <div style={{ paddingBottom: 24 }}>
      <div
        style={{
          padding: "12px 16px",
          display: "flex",
          alignItems: "center",
          gap: 8,
          background: "var(--bg-section)",
          borderRadius: "var(--r-md)",
          margin: "12px 16px 8px",
        }}
      >
        <Search size={18} color="var(--fg3)" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Найти игрока"
          {...noAutofill}
          style={{
            flex: 1,
            border: 0,
            outline: 0,
            background: "transparent",
            fontFamily: "var(--font-body)",
            fontSize: 15,
            color: "var(--fg1)",
          }}
        />
      </div>

      {myRank && !isSearching && (
        <div
          role="button"
          tabIndex={0}
          onClick={() => openProfile(myRank.id)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              openProfile(myRank.id);
            }
          }}
          style={{
            background: "var(--brand-orange-ghost)",
            borderRadius: "var(--r-lg)",
            margin: "0 16px 16px",
            padding: "10px 14px",
            display: "flex",
            alignItems: "center",
            gap: 12,
            cursor: "pointer",
          }}
        >
          <div
            style={{
              width: 32,
              textAlign: "center",
              fontFamily: "var(--font-display)",
              fontWeight: 800,
              fontSize: 18,
              color: "var(--brand-orange)",
            }}
          >
            {myRank.rank}
          </div>
          <Avatar
            initials={getInitials(myRank.first_name, myRank.last_name, myRank.username)}
            size={36}
            tint={tintForId(myRank.id)}
            src={myRank.photo_url}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="list-title">Твоё место</div>
            <div className="caption" style={{ color: "var(--fg2)" }}>
              Рейтинг {myRank.rating}
            </div>
          </div>
        </div>
      )}

      {isLoading && <Placeholder text="Загружаем лидерборд…" />}

      {top3.length > 0 && (
        <>
          <div style={{ height: 12 }} />
          <ListGroup>
            {top3.map((e) => <LeaderRow key={e.id} entry={e} highlight />)}
          </ListGroup>
        </>
      )}

      {rest.length > 0 && (
        <>
          {!isSearching && <div style={{ height: 16 }} />}
          <ListGroup>
            {rest.map((e) => <LeaderRow key={e.id} entry={e} />)}
          </ListGroup>
        </>
      )}

      {/* Infinite-scroll sentinel. Kept a touch above the bottom edge so
          we prefetch while the user is still scrolling. */}
      {hasNextPage && <div ref={sentinelRef} style={{ height: 1 }} />}

      {isFetchingNextPage && <Placeholder text="Подгружаем…" />}

      {!isLoading && entries.length === 0 && (
        <Placeholder
          text={isSearching ? "Никого не нашли." : "Лидерборд пуст."}
        />
      )}

      {/* Subtle hint that we're showing stale rows while a new search is
          in-flight. `isPlaceholderData` is only true during key transitions,
          so this won't blink on the 30 s background refetch. */}
      {isPlaceholderData && entries.length > 0 && (
        <div style={{ padding: "8px 16px", textAlign: "center", color: "var(--fg3)", fontSize: 12 }}>
          Ищу…
        </div>
      )}
    </div>
  );
}

function LeaderRow({
  entry,
  highlight,
}: {
  entry: LeaderboardEntry;
  highlight?: boolean;
}) {
  const openProfile = useOpenUserProfile();
  const medal = ["🥇", "🥈", "🥉"][entry.rank - 1] ?? null;
  const displayName =
    [entry.first_name, entry.last_name].filter(Boolean).join(" ") ||
    entry.username ||
    "Игрок";
  return (
    <ListRow
      onClick={() => openProfile(entry.id)}
      leading={
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 28,
              textAlign: "center",
              fontFamily: "var(--font-display)",
              fontWeight: 700,
              fontSize: 15,
              color: highlight ? "var(--brand-orange)" : "var(--fg3)",
            }}
          >
            {medal ?? entry.rank}
          </div>
          <Avatar
            initials={getInitials(entry.first_name, entry.last_name, entry.username)}
            size={36}
            tint={tintForId(entry.id)}
            src={entry.photo_url}
          />
        </div>
      }
      title={
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, minWidth: 0 }}>
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              minWidth: 0,
            }}
          >
            {displayName}
          </span>
          {entry.is_admin && <Badge kind="brand">Админ</Badge>}
          {entry.is_trainer && <Badge kind="success">Тренер</Badge>}
        </span>
      }
      subtitle={entry.username ? `@${entry.username}` : undefined}
      trailing={
        <div
          title={entry.matches_played === 0 ? "Стартовый рейтинг — нет подтверждённых матчей" : undefined}
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 800,
            fontSize: 17,
            color: entry.matches_played === 0 ? "var(--fg3)" : "var(--fg1)",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {entry.rating}
        </div>
      }
    />
  );
}

function Placeholder({ text }: { text: string }) {
  return (
    <div style={{ padding: "48px 16px", textAlign: "center", color: "var(--fg2)" }}>
      {text}
    </div>
  );
}
